// ksm_frontend containers/projectmng/billplan/BillPlanReplanContainer.js 포팅 (2026-10-21, bito).
// 서버는 ksm_backend BillPlanReplanService 포팅분(com.pms.backend.service.project.order.
// BillPlanReplanService) - 발행 확정/결재 진행중 회차는 잠가 보존하고 나머지만 재분배한다.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Typography,
  Divider,
  TextField,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import * as billReplanApi from '../../../lib/api/billReplan';

/** 분할 개수 상한. 없으면 실수로 1000을 넣어 브라우저가 멈춘다. */
const MAX_SPLIT = 60;

/**
 * 남은 금액을 n회로 나눈다.
 * 나누어떨어지지 않는 잔돈은 마지막 회차에 몰아준다 —
 * 첫 회차에 붙이면 첫 줄만 금액이 튀어 입력 실수처럼 보인다.
 *
 * 날짜는 기준일부터 매월 말일. 기준일이 과거면 오늘로 당긴다.
 */
const buildDraft = (count, totalAmt, baseDateStr) => {
  const n = Math.max(1, count);
  const base = Math.floor(totalAmt / n);
  const remainder = totalAmt - base * n;

  let baseDate = baseDateStr ? dayjs(baseDateStr) : dayjs();
  if (!baseDate.isValid() || baseDate.isBefore(dayjs(), 'day')) baseDate = dayjs();

  return Array.from({ length: n }).map((_, i) => {
    const invAmt = base + (i === n - 1 ? remainder : 0);
    const date = baseDate.add(i, 'month').endOf('month').format('YYYY-MM-DD');
    const invVat = Math.round(invAmt * 0.1);
    return {
      key: `draft-${i}`,
      reqPlanDt: date,
      procPlanDt: date,
      rsltCompDt: date,
      invAmt,
      invVat,
      invTot: invAmt + invVat,
      evidenceType: 'TAX_INVOICE',
    };
  });
};

const fmt = (value) => {
  const n = Number(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n.toLocaleString('ko-KR') : '0';
};
const num = (value) => {
  const n = Number(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

/**
 * 프로젝트의 업체별 계산서 현황.
 *
 * 원가 → 계산서 계획 → 회차 → 발행 → 수금이 한 줄로 이어진다.
 * 업체별 금액이 원가에서 그대로 넘어오기 때문에, 원가를 따로 보고 계산서를 따로 보면
 * 매번 두 화면을 맞춰봐야 한다. 여기서 한 번에 본다.
 *
 * 업체를 고르면 아래에 그 업체의 회차가 나온다.
 *   발행 완료  이미 확정돼 손댈 수 없는 회차
 *   계획 중    아직 안 나간 회차 — 재분배 대상
 */
const BillPlanReplanContainer = ({ prjUuid, entryPoint = billReplanApi.ENTRY_PROJECT }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  // 재분배 편집 상태. 저장 전까지는 화면에서만 바뀐다 —
  // 숫자 하나 잘못 만졌다고 계획이 날아가면 안 된다.
  const [editing, setEditing] = useState(false);
  const [draftRows, setDraftRows] = useState([]);
  const [splitCount, setSplitCount] = useState(1);
  const [saving, setSaving] = useState(false);

  const showError = useCallback((err, fallback) => {
    const message = err?.response?.data?.message || err?.message || fallback;
    enqueueSnackbar(message, { variant: 'error' });
  }, [enqueueSnackbar]);

  const load = useCallback(async () => {
    if (!prjUuid) return;
    setLoading(true);
    try {
      const res = await billReplanApi.getReplanGroups(prjUuid, entryPoint);
      // 총액이 0인 묶음은 나눌 게 없다. 줄만 차지하고 누를 수도 없다.
      const list = (res?.data?.list || []).filter((g) => num(g.billAmt) > 0);
      setGroups(list);
      setSelectedId((prev) =>
        prev && list.some((g) => g.billUuid === prev) ? prev : list[0]?.billUuid || null,
      );
    } catch (err) {
      showError(err, '발행계획을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [prjUuid, entryPoint, showError]);

  useEffect(() => { load(); }, [load]);

  const selected = useMemo(
    () => groups.find((g) => g.billUuid === selectedId) || null,
    [groups, selectedId],
  );

  const startEdit = useCallback(() => {
    if (!selected) return;
    const rows = selected.replanRows || [];
    setDraftRows(rows.map((r, i) => ({
      key: r.invUuid || `row-${i}`,
      reqPlanDt: r.reqPlanDt,
      procPlanDt: r.procPlanDt,
      rsltCompDt: r.rsltCompDt,
      invAmt: num(r.invAmt),
      invVat: num(r.invVat),
      invTot: num(r.invTot),
      evidenceType: r.evidenceType || 'TAX_INVOICE',
    })));
    setSplitCount(rows.length || 1);
    setEditing(true);
  }, [selected]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
    setDraftRows([]);
  }, []);

  /** 분할 숫자로 다시 나눈다. 직접 고쳐둔 값은 여기서 지워진다. */
  const applySplit = useCallback(() => {
    const count = Number(splitCount);
    if (!Number.isInteger(count) || count < 1) {
      enqueueSnackbar('분할 개수를 1 이상으로 입력해 주세요.', { variant: 'warning' });
      return;
    }
    if (count > MAX_SPLIT) {
      enqueueSnackbar(`분할 개수는 최대 ${MAX_SPLIT}회까지 가능합니다.`, { variant: 'warning' });
      return;
    }
    const total = num(selected?.replanAmt);
    if (total <= 0) {
      enqueueSnackbar('나눌 금액이 없습니다.', { variant: 'warning' });
      return;
    }
    // 확정된 회차 다음 달부터 잡는다. 없으면 오늘 기준.
    const lastLocked = (selected?.lockedRows || []).slice(-1)[0];
    const baseDate = lastLocked?.reqPlanDt
      ? dayjs(lastLocked.reqPlanDt).add(1, 'month').format('YYYY-MM-DD')
      : null;
    setDraftRows(buildDraft(count, total, baseDate));
  }, [splitCount, selected, enqueueSnackbar]);

  const changeRow = useCallback((key, field, value) => {
    setDraftRows((prev) => prev.map((r) => {
      if (r.key !== key) return r;
      const next = { ...r, [field]: value };
      if (field === 'invAmt') {
        const amt = num(value);
        next.invAmt = amt;
        // 세금계산서는 부가세가 10%로 따라온다. 금액을 고치면 같이 맞춘다.
        next.invVat = Math.round(amt * 0.1);
      }
      next.invTot = num(next.invAmt) + num(next.invVat);
      return next;
    }));
  }, []);

  const draftSum = useMemo(
    () => draftRows.reduce((acc, r) => acc + num(r.invAmt), 0),
    [draftRows],
  );
  const diffAmt = num(selected?.replanAmt) - draftSum;
  const balanced = diffAmt === 0;

  const handleSave = useCallback(async () => {
    if (!selected || !balanced) return;
    if (draftRows.some((r) => num(r.invAmt) <= 0)) {
      enqueueSnackbar('회차 금액은 0보다 커야 합니다.', { variant: 'warning' });
      return;
    }
    if (draftRows.some((r) => !r.reqPlanDt)) {
      enqueueSnackbar('발행 예정일을 입력해 주세요.', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      await billReplanApi.saveReplan(selected.billUuid, {
        rows: draftRows.map((r) => ({
          reqPlanDt: r.reqPlanDt,
          procPlanDt: r.procPlanDt || r.reqPlanDt,
          rsltCompDt: r.rsltCompDt || r.reqPlanDt,
          invAmt: String(num(r.invAmt)),
          invVat: String(num(r.invVat)),
          invTot: String(num(r.invTot)),
          evidenceType: r.evidenceType || 'TAX_INVOICE',
        })),
        entryPoint,
      });
      enqueueSnackbar('발행계획을 재분배했습니다.', { variant: 'success' });
      cancelEdit();
      load();
    } catch (err) {
      showError(err, '재분배에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  }, [selected, balanced, draftRows, entryPoint, enqueueSnackbar, cancelEdit, load, showError]);

  if (!prjUuid) return null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const term = selected?.billTyp === '매입' ? '지급' : '수금';

  return (
    <Box sx={{ width: '100%', p: 1 }}>
      {/* ── 업체별 요약 ── */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 700 }}>구분</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>업체</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>원가</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>계산서 계획</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>차이</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>회차</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>확정</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.disabled' }}>
                  계산서 계획이 없습니다.
                </TableCell>
              </TableRow>
            ) : groups.map((g) => {
              const lockedCnt = (g.lockedRows || []).length;
              const replanCnt = (g.replanRows || []).length;
              // 원가와 계획이 어긋나면 원가만 고치고 계산서는 안 고친 것이다.
              const diff = num(g.costAmt) - num(g.billAmt);
              const isSelected = g.billUuid === selectedId;
              return (
                <TableRow
                  key={g.billUuid}
                  hover
                  selected={isSelected}
                  onClick={() => { setSelectedId(g.billUuid); cancelEdit(); }}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Chip
                      label={g.billTyp}
                      size="small"
                      variant="outlined"
                      color={g.billTyp === '매출' ? 'primary' : 'warning'}
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell sx={isSelected ? { fontWeight: 700 } : undefined}>
                    {g.partnerNm}
                  </TableCell>
                  <TableCell align="right">{fmt(g.costAmt)}</TableCell>
                  <TableCell align="right">{fmt(g.billAmt)}</TableCell>
                  <TableCell align="right">
                    {diff === 0 ? (
                      <Typography variant="body2" color="text.disabled">-</Typography>
                    ) : (
                      <Tooltip title="원가와 계산서 계획 금액이 다릅니다">
                        <Typography variant="body2" color="error.main" sx={{ fontWeight: 700 }}>
                          {fmt(diff)}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="center">{lockedCnt + replanCnt}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color={lockedCnt ? 'text.primary' : 'text.disabled'}>
                      {fmt(g.lockedAmt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── 선택한 업체의 회차 ── */}
      {selected && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {selected.partnerNm} · {selected.billTyp}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              계획 {fmt(selected.billAmt)}
            </Typography>
            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 700 }}>
              분배 가능 {fmt(selected.replanAmt)}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            {/* 권한이 없으면 왜 안 되는지 보이게 한다. 버튼만 사라지면 고장으로 읽힌다. */}
            {editing ? (
              <>
                <Button size="small" onClick={cancelEdit} disabled={saving}>취소</Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSave}
                  disabled={!balanced || saving}
                >
                  저장
                </Button>
              </>
            ) : (
              /* 권한이 없으면 왜 안 되는지 보이게 한다. 버튼만 사라지면 고장으로 읽힌다. */
              <Tooltip title={selected.canReplan ? '' : (selected.permissionReason || '')}>
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={!selected.canReplan}
                    onClick={startEdit}
                  >
                    발행계획 재분배
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>

          {/* 계산서 금액이 어디서 나왔는지. 원가 표를 따로 두지 않고 여기서 근거를 보여준다. */}
          {(selected.costItems || []).length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                원가 항목 ({selected.costItems.length}건)
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                      <TableCell sx={{ fontWeight: 700 }}>항목</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>수량</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>매출금액</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>매입금액</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selected.costItems.map((c, i) => (
                      <TableRow key={c.expUuid || i}>
                        <TableCell>{c.productNm || '-'}</TableCell>
                        <TableCell align="center">
                          {c.expCnt || 0}{c.productUnit ? ` ${c.productUnit}` : ''}
                        </TableCell>
                        <TableCell align="right">{fmt(c.saleVal)}</TableCell>
                        <TableCell align="right">{fmt(c.buyVal)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          <InstallmentTable
            title="발행 완료"
            rows={selected.lockedRows || []}
            emptyText="아직 발행된 회차가 없습니다."
            locked
          />
          <Divider sx={{ my: 1.5 }} />
          {editing ? (
            <Box>
              {/* 숫자를 바꿔도 바로 반영하지 않는다. [균등분할] 을 눌러야 다시 나눈다. */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">분할</Typography>
                <TextField
                  size="small"
                  value={splitCount}
                  onChange={(e) => {
                    const raw = String(e.target.value).replace(/[^0-9]/g, '');
                    setSplitCount(raw === '' ? '' : Math.min(Number(raw), MAX_SPLIT));
                  }}
                  inputProps={{ inputMode: 'numeric', style: { textAlign: 'center' } }}
                  sx={{ width: 70 }}
                />
                <Typography variant="caption" color="text.secondary">회</Typography>
                <Button size="small" variant="outlined" onClick={applySplit}>균등분할</Button>
                <Box sx={{ flexGrow: 1 }} />
                <Chip
                  size="small"
                  label={balanced ? '금액 일치' : `차액 ${fmt(diffAmt)}`}
                  color={balanced ? 'success' : 'error'}
                  variant={balanced ? 'outlined' : 'filled'}
                  sx={{ fontWeight: 700 }}
                />
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                      <TableCell sx={{ fontWeight: 700, width: 60 }}>회차</TableCell>
                      <TableCell sx={{ fontWeight: 700, minWidth: 150 }}>발행 예정일</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, minWidth: 140 }}>공급가액</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>VAT</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>합계</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {draftRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 2, color: 'text.disabled' }}>
                          분할 횟수를 넣고 균등분할을 눌러 주세요.
                        </TableCell>
                      </TableRow>
                    ) : draftRows.map((r, idx) => (
                      <TableRow key={r.key}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              value={r.reqPlanDt ? dayjs(r.reqPlanDt) : null}
                              onChange={(v) => changeRow(r.key, 'reqPlanDt',
                                v && v.isValid() ? v.format('YYYY-MM-DD') : null)}
                              format="YYYY-MM-DD"
                              slotProps={{ textField: { size: 'small', variant: 'standard' } }}
                            />
                          </LocalizationProvider>
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            variant="standard"
                            value={fmt(r.invAmt)}
                            onChange={(e) => changeRow(r.key, 'invAmt',
                              String(e.target.value).replace(/[^0-9]/g, ''))}
                            inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric' }}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell align="right">{fmt(r.invVat)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(r.invTot)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {!balanced && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  분배 가능액 {fmt(selected.replanAmt)} 과 {fmt(Math.abs(diffAmt))} 차이가 납니다. 맞아야 저장됩니다.
                </Typography>
              )}
            </Box>
          ) : (
            <InstallmentTable
              title="계획 중"
              rows={selected.replanRows || []}
              emptyText="계획된 회차가 없습니다."
              term={term}
            />
          )}
        </Box>
      )}

    </Box>
  );
};

/** 회차 목록. 확정분과 계획분을 같은 모양으로 보여주되 잠금 사유만 다르게 표시한다. */
function InstallmentTable({ title, rows, emptyText, locked, term }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {title} ({rows.length}건)
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 700, width: 60 }}>회차</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>발행 예정일</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>공급가액</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>VAT</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>합계</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{locked ? '상태' : `${term || '수금'} 여부`}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 2, color: 'text.disabled' }}>
                  {emptyText}
                </TableCell>
              </TableRow>
            ) : rows.map((r, idx) => (
              <TableRow key={r.invUuid || idx} sx={locked ? { backgroundColor: 'action.hover' } : undefined}>
                <TableCell>{idx + 1}</TableCell>
                <TableCell>{r.reqPlanDt || '-'}</TableCell>
                <TableCell align="right">{fmt(r.invAmt)}</TableCell>
                <TableCell align="right">{fmt(r.invVat)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(r.invTot)}</TableCell>
                <TableCell>
                  {locked ? (
                    <Chip size="small" label={r.lockReason || '발행 확정'} variant="outlined" />
                  ) : (
                    <Typography variant="body2" color="text.secondary">미발행</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default BillPlanReplanContainer;
